import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';

/**
 * StudyCodeSheet - 2025 FaceTime/Cash App Style Share Experience
 *
 * Design Principles:
 * - FaceTime SharePlay: Full-screen modal with massive code
 * - Cash App: Giant beautiful typography, QR for quick scanning
 * - Discord: Clean invite modal, one-tap copy
 * - Apple HIG: Native share sheet integration
 *
 * Target: 6-12 grade students who share everything via iMessage/Snap
 */
const StudyCodeSheet = ({
  isOpen,
  onClose,
  code,
  expiresAt,           // ISO timestamp
  hostName,            // "Emma's session"
  subject,             // "Math homework"
  onRegenerateCode,    // Callback to get new code
  joinUrl,             // Full URL for sharing (e.g., https://teachtappy.com/join/2847)
}) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  // Calculate time remaining
  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining('24:00:00');
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Copy code to clipboard
  const handleCopy = useCallback(async () => {
    const shareText = `Join my study session on Tappy!\n\nCode: ${code}\n\nOr tap: ${joinUrl || `https://teachtappy.com/join/${code}`}`;

    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [code, joinUrl]);

  // Native share (Web Share API)
  const handleShare = useCallback(async () => {
    const shareData = {
      title: 'Join my study session!',
      text: `Study with me on Tappy! Code: ${code}`,
      url: joinUrl || `https://teachtappy.com/join/${code}`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to copy
          handleCopy();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      handleCopy();
    }
  }, [code, joinUrl, handleCopy]);

  // Platform-specific share shortcuts
  const shareToiMessage = () => {
    const text = encodeURIComponent(`Join my study session on Tappy! Code: ${code} ${joinUrl || `https://teachtappy.com/join/${code}`}`);
    window.open(`sms:&body=${text}`, '_blank');
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`Join my study session on Tappy!\n\nCode: ${code}\n${joinUrl || `https://teachtappy.com/join/${code}`}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareToSnapchat = () => {
    // Snapchat doesn't have a direct share URL, use native share or copy
    handleShare();
  };

  // Format code with spaces for readability (2847 -> 28 47)
  const formatCode = (c) => {
    if (!c) return '';
    const str = c.toString();
    if (str.length === 4) {
      return `${str.slice(0, 2)} ${str.slice(2)}`;
    }
    return str;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '16px',
          }}
        >
          <motion.div
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, #1c1c1e 0%, #0a0a0b 100%)',
              borderRadius: '28px',
              width: '100%',
              maxWidth: '380px',
              overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 20px 0',
            }}>
              <div style={{ width: '32px' }} /> {/* Spacer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                {/* Study icon */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #297A92 0%, #42909E 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                    <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                  </svg>
                </div>
                <span style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'white',
                }}>
                  Study Session
                </span>
              </div>
              {/* Close button */}
              <button
                onClick={onClose}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '18px',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Session context */}
            {(hostName || subject) && (
              <div style={{
                textAlign: 'center',
                padding: '12px 20px 0',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  color: 'rgba(255, 255, 255, 0.5)',
                }}>
                  {hostName ? `${hostName}'s session` : subject || 'Homework help'}
                </p>
              </div>
            )}

            {/* Main Code Display */}
            <div style={{
              padding: '32px 20px',
              textAlign: 'center',
            }}>
              {/* Code container with subtle glow */}
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                style={{
                  background: 'rgba(41, 122, 146, 0.1)',
                  border: '2px solid rgba(41, 122, 146, 0.3)',
                  borderRadius: '20px',
                  padding: '24px 32px',
                  marginBottom: '16px',
                  boxShadow: '0 0 40px rgba(41, 122, 146, 0.15)',
                }}
              >
                <div style={{
                  fontSize: '72px',
                  fontWeight: '700',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Mono", monospace',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '12px',
                  color: 'white',
                  textShadow: '0 2px 20px rgba(41, 122, 146, 0.3)',
                  lineHeight: 1,
                }}>
                  {formatCode(code)}
                </div>
              </motion.div>

              {/* Expiry timer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                color: timeRemaining === 'Expired' ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
                fontSize: '14px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>
                  {timeRemaining === 'Expired' ? 'Code expired' : `Expires in ${timeRemaining}`}
                </span>
              </div>
            </div>

            {/* Primary Actions */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              padding: '0 20px',
            }}>
              {/* Copy Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCopy}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: copied ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {copied ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                )}
                <span style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                }}>
                  {copied ? 'Copied!' : 'Copy'}
                </span>
              </motion.button>

              {/* Share Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleShare}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: '#297A92',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
                <span style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                }}>
                  Share
                </span>
              </motion.button>
            </div>

            {/* QR Code Toggle */}
            <div style={{
              padding: '20px',
            }}>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => setShowQR(!showQR)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
                {showQR ? 'Hide QR Code' : 'Show QR Code'}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showQR ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </motion.button>

              {/* QR Code */}
              <AnimatePresence>
                {showQR && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      overflow: 'hidden',
                      marginTop: '12px',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '20px',
                      background: 'white',
                      borderRadius: '16px',
                    }}>
                      <QRCodeSVG
                        value={joinUrl || `https://teachtappy.com/join/${code}`}
                        size={180}
                        level="M"
                        includeMargin={false}
                        fgColor="#0a0a0b"
                        bgColor="white"
                      />
                    </div>
                    <p style={{
                      textAlign: 'center',
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: '12px 0 0',
                    }}>
                      Scan to join instantly
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Platform Shortcuts */}
            <div style={{
              padding: '0 20px 24px',
            }}>
              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  or send directly
                </span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
              </div>

              {/* Platform buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
              }}>
                {/* iMessage */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={shareToiMessage}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, #5AC8FA 0%, #34AADC 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(52, 170, 220, 0.3)',
                  }}
                  title="iMessage"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2C6.486 2 2 6.262 2 11.5c0 2.012.644 3.87 1.744 5.396L2 22l5.104-1.744A10.18 10.18 0 0012 21c5.514 0 10-4.262 10-9.5S17.514 2 12 2z"/>
                  </svg>
                </motion.button>

                {/* WhatsApp */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={shareToWhatsApp}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, #25D366 0%, #128C7E 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                  }}
                  title="WhatsApp"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824z"/>
                  </svg>
                </motion.button>

                {/* Snapchat */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={shareToSnapchat}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, #FFFC00 0%, #FFE500 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(255, 252, 0, 0.3)',
                  }}
                  title="Snapchat"
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#0a0a0b">
                    <path d="M12.166 3c.796 0 3.495.223 4.769 2.807.477.967.366 2.503.278 3.783l-.007.11c-.025.388-.05.778-.05 1.063 0 .154.023.283.168.346.584.254.924.4 1.063.496.358.245.396.575.396.71 0 .428-.354.73-.686.813-.11.028-.227.048-.344.068l-.018.003c-.314.053-.67.114-.994.293-.087.048-.17.107-.248.177.206.477.62 1.098 1.314 1.623 1.058.798 2.19.988 2.537 1.043l.01.001c.136.022.283.063.356.174.073.11.049.266.015.379-.108.357-.438.63-.83.786-.178.07-.366.126-.556.168l-.101.023c-.212.048-.433.099-.541.144-.112.047-.128.07-.142.158-.024.157.043.363.096.528.053.166.108.338.087.523-.026.229-.172.408-.429.533-.315.152-.744.228-1.312.23-.307.001-.614-.018-.921-.038l-.094-.006c-.265-.018-.54-.036-.807-.025-.287.012-.496.074-.752.265-.604.452-1.217.946-2.353.946-1.172 0-1.784-.516-2.392-.963-.262-.193-.465-.252-.727-.264-.267-.011-.545.007-.81.025l-.094.006c-.307.02-.614.039-.921.038-.568-.002-.997-.078-1.312-.23-.257-.125-.403-.304-.429-.533-.021-.185.034-.357.087-.523.053-.165.12-.37.096-.528-.014-.088-.03-.11-.142-.158-.108-.045-.329-.096-.541-.144l-.101-.023c-.19-.042-.378-.098-.556-.168-.392-.156-.722-.429-.83-.786-.034-.113-.058-.269.015-.379.073-.111.22-.152.356-.174l.01-.001c.347-.055 1.479-.245 2.537-1.043.694-.525 1.108-1.146 1.314-1.623-.078-.07-.161-.129-.248-.177-.324-.179-.68-.24-.994-.293l-.018-.003c-.117-.02-.234-.04-.344-.068-.332-.083-.686-.385-.686-.813 0-.135.038-.465.396-.71.139-.096.479-.242 1.063-.496.145-.063.168-.192.168-.346 0-.285-.025-.675-.05-1.063l-.007-.11c-.088-1.28-.199-2.816.278-3.783C8.671 3.223 11.37 3 12.166 3z"/>
                  </svg>
                </motion.button>
              </div>
            </div>

            {/* Regenerate option for expired codes */}
            {timeRemaining === 'Expired' && onRegenerateCode && (
              <div style={{
                padding: '0 20px 24px',
              }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onRegenerateCode}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#297A92',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Generate New Code
                </motion.button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StudyCodeSheet;
